import type {
  LevelTilemap,
  LevelDecoration,
  ObjectDecoration,
  DecorationTileType,
} from "@/types/tileset.types"
import { DECORATION_DEFS } from "@/types/tileset.types"
import { GRID_WIDTH, GRID_HEIGHT, TILE_SIZE } from "@/data/game-settings"
import { rasterizePath, buildGroundTiles, pathTileSet, subtileKey } from "./autotile"

// ============================================
// PROCEDURAL LEVEL TILEMAP GENERATOR
// ============================================
// Builds a complete tilemap (autotiled ground + seeded decorations) from a
// level's existing path definition. Placement is deterministic per level id,
// so a level always looks the same.
//
// Themes are data-only: when per-biome object sheets are added later, a theme
// only needs its object lists swapped.

const OBJ = "/images/biomes/grassland/objects"

/** A placeable object PNG with its footprint in logical tiles */
interface ThemeObject {
  image: string
  size: 1 | 2 | 4
  /** Relative pick weight within its size class */
  weight?: number
}

export interface LevelTheme {
  /** Large 4x4 props (trees, big ruins). count = how many to place */
  large: ThemeObject[]
  largeCount: number
  /** Medium 2x2 props */
  medium: ThemeObject[]
  mediumCount: number
  /** Small 1x1 props */
  small: ThemeObject[]
  smallCount: number
  /** Sheet-based decorations (crystals, rocks, flowers, ground texture) */
  details: Array<{ type: DecorationTileType; weight?: number }>
  detailCount: number
}

// --- deterministic RNG (mulberry32) ---
function makeRng(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function hashString(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function pickWeighted<T extends { weight?: number }>(rng: () => number, items: T[]): T {
  const total = items.reduce((sum, it) => sum + (it.weight ?? 1), 0)
  let roll = rng() * total
  for (const it of items) {
    roll -= it.weight ?? 1
    if (roll <= 0) return it
  }
  return items[items.length - 1]
}

// Gameplay happens in the top part of the 60-tile-tall world (paths end
// around row 26). Uniform placement left the visible map sparse, so most
// decorations are sampled from the play area with a light scatter below.
const PLAY_AREA_ROWS = 30
function sampleRow(rng: () => number, itemHeight: number): number {
  if (rng() < 0.8) {
    return Math.floor(rng() * Math.min(PLAY_AREA_ROWS, GRID_HEIGHT - itemHeight))
  }
  return Math.floor(rng() * (GRID_HEIGHT - itemHeight))
}

interface GenerateArgs {
  levelId: string
  /** Path waypoints in world px (same array handed to LevelConfig.path) */
  path: Array<{ x: number; y: number }>
  pathWidth: number
  landingTile: { x: number; y: number }
  basePosition: { x: number; y: number }
  theme: LevelTheme
}

export function generateTilemap(args: GenerateArgs): LevelTilemap {
  const rng = makeRng(hashString(args.levelId))
  // Visual band: 1-tile gameplay paths are drawn 3 subtiles (48px) wide so the
  // road gets a solid dirt lane between its fringes. Gameplay collision keeps
  // using the level's real pathWidth - the extra 8px per side is only art.
  const visualWidth = args.pathWidth <= TILE_SIZE ? args.pathWidth * 1.5 : args.pathWidth
  const pathSubtiles = rasterizePath(args.path, visualWidth)
  const pathTiles = pathTileSet(pathSubtiles)

  // --- keep-out map (logical tiles) ---
  // blocked: no object may overlap. Includes the path plus a 1-tile shoulder
  // (keeps tower space next to the road), the landing zone, and the base.
  const blocked = new Set<number>()
  const blockTile = (tx: number, ty: number) => blocked.add(subtileKey(tx, ty))
  const blockArea = (tx: number, ty: number, radius: number) => {
    for (let dy = -radius; dy <= radius; dy++)
      for (let dx = -radius; dx <= radius; dx++) blockTile(tx + dx, ty + dy)
  }

  for (const key of pathTiles) blocked.add(key)
  // 1-tile shoulder around the path
  for (let ty = 0; ty < GRID_HEIGHT; ty++) {
    for (let tx = 0; tx < GRID_WIDTH; tx++) {
      if (!pathTiles.has(subtileKey(tx, ty))) continue
      for (let dy = -1; dy <= 1; dy++)
        for (let dx = -1; dx <= 1; dx++) blockTile(tx + dx, ty + dy)
    }
  }
  blockArea(args.landingTile.x, args.landingTile.y, 3)
  blockArea(Math.floor(args.basePosition.x / TILE_SIZE), Math.floor(args.basePosition.y / TILE_SIZE), 2)

  const isFree = (tx: number, ty: number, w: number, h: number, margin: number): boolean => {
    if (tx < 0 || ty < 0 || tx + w > GRID_WIDTH || ty + h > GRID_HEIGHT) return false
    for (let dy = -margin; dy < h + margin; dy++) {
      for (let dx = -margin; dx < w + margin; dx++) {
        if (blocked.has(subtileKey(tx + dx, ty + dy))) return false
      }
    }
    return true
  }
  const claim = (tx: number, ty: number, w: number, h: number) => {
    for (let dy = 0; dy < h; dy++) for (let dx = 0; dx < w; dx++) blockTile(tx + dx, ty + dy)
  }

  // --- object placement ---
  const objectDecorations: ObjectDecoration[] = []
  const placeObjects = (
    pool: ThemeObject[],
    count: number,
    size: number,
    margin: number,
    zIndex: number,
  ) => {
    if (pool.length === 0) return
    let placed = 0
    let attempts = 0
    const maxAttempts = count * 40
    while (placed < count && attempts < maxAttempts) {
      attempts++
      const tx = Math.floor(rng() * (GRID_WIDTH - size))
      const ty = sampleRow(rng, size)
      if (!isFree(tx, ty, size, size, margin)) continue
      const obj = pickWeighted(rng, pool)
      objectDecorations.push({
        imagePath: obj.image,
        tileX: tx,
        tileY: ty,
        widthTiles: obj.size,
        heightTiles: obj.size,
        zIndex,
      })
      claim(tx, ty, size, size)
      placed++
    }
  }

  // Big to small, so large props get first pick of open space
  placeObjects(args.theme.large, args.theme.largeCount, 4, 1, 10)
  placeObjects(args.theme.medium, args.theme.mediumCount, 2, 1, 8)
  placeObjects(args.theme.small, args.theme.smallCount, 1, 0, 6)

  // --- sheet-based detail decorations ---
  // Blocking details reserve their tiles like objects. Cosmetic details may
  // sit anywhere on grass (including the path shoulder) except on claimed
  // tiles, so the map reads busy without eating tower space.
  const decorations: LevelDecoration[] = []
  const detailPlacedAt = new Set<number>()
  let detailPlaced = 0
  let detailAttempts = 0
  const detailMax = args.theme.detailCount * 30
  while (detailPlaced < args.theme.detailCount && detailAttempts < detailMax && args.theme.details.length > 0) {
    detailAttempts++
    const pick = pickWeighted(rng, args.theme.details)
    const def = DECORATION_DEFS[pick.type]
    const w = def.coords.width ?? 1
    const h = def.coords.height ?? 1
    const tx = Math.floor(rng() * (GRID_WIDTH - w))
    const ty = sampleRow(rng, h)

    if (def.buildable && !def.impassable) {
      // Cosmetic: only needs to stay off the path and off other art
      let ok = true
      for (let dy = 0; dy < h && ok; dy++)
        for (let dx = 0; dx < w && ok; dx++) {
          const k = subtileKey(tx + dx, ty + dy)
          if (pathTiles.has(k) || detailPlacedAt.has(k)) ok = false
        }
      // Also stay out from under placed objects
      if (ok) {
        for (const o of objectDecorations) {
          if (tx < o.tileX + o.widthTiles && o.tileX < tx + w && ty < o.tileY + o.heightTiles && o.tileY < ty + h) {
            ok = false
            break
          }
        }
      }
      if (!ok) continue
    } else {
      // Blocking prop: same rules as objects
      if (!isFree(tx, ty, w, h, 0)) continue
      claim(tx, ty, w, h)
    }

    for (let dy = 0; dy < h; dy++)
      for (let dx = 0; dx < w; dx++) detailPlacedAt.add(subtileKey(tx + dx, ty + dy))
    decorations.push({ type: pick.type, tileX: tx, tileY: ty })
    detailPlaced++
  }

  return {
    width: GRID_WIDTH,
    height: GRID_HEIGHT,
    tiles: buildGroundTiles(pathSubtiles),
    decorations,
    objectDecorations,
  }
}

// ============================================
// SHARED OBJECT POOLS
// ============================================

const TREES: ThemeObject[] = [
  { image: `${OBJ}/Tree1.png`, size: 4 },
  { image: `${OBJ}/Tree2.png`, size: 4 },
  { image: `${OBJ}/Tree3.png`, size: 4 },
  { image: `${OBJ}/Tree4.png`, size: 4 },
]
const RUINS_LARGE: ThemeObject[] = [
  { image: `${OBJ}/Ruin1_grass_shadow.png`, size: 4 },
  { image: `${OBJ}/Ruin2_grass_shadow.png`, size: 4 },
]
const RUINS_MEDIUM: ThemeObject[] = [
  { image: `${OBJ}/Ruin3_grass_shadow.png`, size: 2 },
  { image: `${OBJ}/Ruin4_grass_shadow.png`, size: 2 },
]
const BUSHES_MEDIUM: ThemeObject[] = [
  { image: `${OBJ}/Bush1.png`, size: 2 },
  { image: `${OBJ}/Bush2.png`, size: 2 },
  { image: `${OBJ}/Bush3.png`, size: 2 },
  { image: `${OBJ}/Bush4.png`, size: 2 },
  { image: `${OBJ}/Bush5.png`, size: 2 },
  { image: `${OBJ}/Bush6.png`, size: 2 },
]
const ROCKS_MEDIUM: ThemeObject[] = [
  { image: `${OBJ}/Rpck_grass1.png`, size: 2 },
  { image: `${OBJ}/Rpck_grass2.png`, size: 2 },
  { image: `${OBJ}/Rpck_grass3.png`, size: 2 },
  { image: `${OBJ}/Rpck_grass4.png`, size: 2 },
]
const STONES_MEDIUM: ThemeObject[] = [
  { image: `${OBJ}/Stone1_grass_shadow.png`, size: 2 },
  { image: `${OBJ}/Stone2_grass_shadow.png`, size: 2 },
]
const BUSHES_SMALL: ThemeObject[] = [
  { image: `${OBJ}/Bush10.png`, size: 1 },
  { image: `${OBJ}/Bush11.png`, size: 1 },
  { image: `${OBJ}/Bush12.png`, size: 1 },
  { image: `${OBJ}/Bush13.png`, size: 1 },
  { image: `${OBJ}/Bush15.png`, size: 1 },
  { image: `${OBJ}/Bush16.png`, size: 1 },
]
const STONES_SMALL: ThemeObject[] = [
  { image: `${OBJ}/Stone3_grass_shadow.png`, size: 1 },
  { image: `${OBJ}/Stone4_grass_shadow.png`, size: 1 },
  { image: `${OBJ}/Stone5_grass_shadow.png`, size: 1 },
  { image: `${OBJ}/Rpck_grass5.png`, size: 1 },
  { image: `${OBJ}/Rock_grass_element11.png`, size: 1 },
  { image: `${OBJ}/Rock_grass_element12.png`, size: 1 },
]
const DEAD_TREES: ThemeObject[] = [
  { image: `${OBJ}/Broken_tree1.png`, size: 1 },
  { image: `${OBJ}/Broken_tree2.png`, size: 1 },
  { image: `${OBJ}/Broken_tree4.png`, size: 1 },
  { image: `${OBJ}/Broken_tree5.png`, size: 1 },
]
const RUIN_SMALL: ThemeObject[] = [{ image: `${OBJ}/Ruin5_grass_shadow.png`, size: 1 }]
const GRASS_ELEMENTS: ThemeObject[] = [
  { image: `${OBJ}/grass_element1.png`, size: 1 },
  { image: `${OBJ}/grass_element2.png`, size: 1 },
  { image: `${OBJ}/grass_element3.png`, size: 1 },
]
const FLOWERS_SMALL: ThemeObject[] = [
  { image: `${OBJ}/Flower1.png`, size: 1 },
  { image: `${OBJ}/Flower3.png`, size: 1 },
  { image: `${OBJ}/Flower5.png`, size: 1 },
  { image: `${OBJ}/Flower7.png`, size: 1 },
  { image: `${OBJ}/Flower9.png`, size: 1 },
]

// Detail palettes
const MEADOW_DETAILS: LevelTheme["details"] = [
  { type: "grass_tufts", weight: 4 },
  { type: "flower_blue", weight: 2 },
  { type: "flowers_orange", weight: 2 },
  { type: "flowers_white", weight: 1 },
  { type: "dirt_speckles", weight: 2 },
]
const BARREN_DETAILS: LevelTheme["details"] = [
  { type: "dirt_speckles", weight: 4 },
  { type: "dirt_mound", weight: 3 },
  { type: "stones_small", weight: 3 },
  { type: "grass_tufts", weight: 2 },
  { type: "dirt_clearing", weight: 1 },
]
const MYSTIC_DETAILS: LevelTheme["details"] = [
  { type: "sparkle", weight: 3 },
  { type: "crystal_blue", weight: 1 },
  { type: "grass_tufts", weight: 2 },
  { type: "dirt_speckles", weight: 2 },
]

// ============================================
// PER-LEVEL THEMES
// ============================================
// One entry per level id. Tuned for readability at gameplay zoom: forests are
// dense, camps and wastelands are open, ruins levels lean on the ruin sets.

export const levelThemes: Record<string, LevelTheme> = {
  // Goblin Camp - scrappy woodland clearing
  level2: {
    large: TREES,
    largeCount: 4,
    medium: [...BUSHES_MEDIUM, ...ROCKS_MEDIUM],
    mediumCount: 8,
    small: [...BUSHES_SMALL, ...DEAD_TREES, ...GRASS_ELEMENTS],
    smallCount: 12,
    details: [...BARREN_DETAILS, { type: "dirt_clearing", weight: 2 }],
    detailCount: 26,
  },
  // Fungal Forest - dense verdant growth
  level3: {
    large: TREES,
    largeCount: 7,
    medium: [...BUSHES_MEDIUM, { image: `${OBJ}/Bush14.png`, size: 2 }, { image: `${OBJ}/Bush17.png`, size: 2 }],
    mediumCount: 11,
    small: [...BUSHES_SMALL, ...GRASS_ELEMENTS],
    smallCount: 14,
    details: [
      { type: "grass_tufts", weight: 4 },
      { type: "bush_berry_blue", weight: 2 },
      { type: "red_plant", weight: 2 },
      { type: "sparkle", weight: 1 },
      { type: "flower_blue", weight: 1 },
    ],
    detailCount: 30,
  },
  // Swamp Fortress - overgrown, waterlogged ruins
  level4: {
    large: [...TREES.slice(0, 2), ...RUINS_LARGE],
    largeCount: 5,
    medium: [...BUSHES_MEDIUM, ...RUINS_MEDIUM],
    mediumCount: 9,
    small: [...BUSHES_SMALL, ...DEAD_TREES, ...RUIN_SMALL],
    smallCount: 12,
    details: [
      { type: "grass_tufts", weight: 4 },
      { type: "red_plant_spiky", weight: 2 },
      { type: "dirt_mound", weight: 2 },
      { type: "dirt_speckles", weight: 2 },
    ],
    detailCount: 26,
  },
  // The Graveyard - sparse, stony, dead
  level5: {
    large: RUINS_LARGE,
    largeCount: 3,
    medium: [...STONES_MEDIUM, ...RUINS_MEDIUM],
    mediumCount: 9,
    small: [...STONES_SMALL, ...DEAD_TREES, ...RUIN_SMALL],
    smallCount: 15,
    details: BARREN_DETAILS,
    detailCount: 24,
  },
  // Orcish Warcamp - trampled open ground
  level6: {
    large: TREES.slice(2),
    largeCount: 2,
    medium: [...ROCKS_MEDIUM, ...STONES_MEDIUM],
    mediumCount: 8,
    small: [...DEAD_TREES, ...STONES_SMALL, ...BUSHES_SMALL.slice(0, 2)],
    smallCount: 13,
    details: [...BARREN_DETAILS, { type: "dirt_clearing", weight: 3 }],
    detailCount: 28,
  },
  // Haunted Ruins - collapsed structures everywhere
  level7: {
    large: RUINS_LARGE,
    largeCount: 5,
    medium: [...RUINS_MEDIUM, ...STONES_MEDIUM],
    mediumCount: 10,
    small: [...RUIN_SMALL, ...STONES_SMALL, ...DEAD_TREES],
    smallCount: 13,
    details: MYSTIC_DETAILS,
    detailCount: 24,
  },
  // Werewolf Den - deep dark forest
  level8: {
    large: TREES,
    largeCount: 7,
    medium: [...BUSHES_MEDIUM, ...ROCKS_MEDIUM],
    mediumCount: 10,
    small: [...BUSHES_SMALL, ...DEAD_TREES],
    smallCount: 13,
    details: [
      { type: "grass_tufts", weight: 4 },
      { type: "dirt_mound", weight: 2 },
      { type: "red_plant", weight: 1 },
      { type: "dirt_speckles", weight: 2 },
    ],
    detailCount: 24,
  },
  // The Foundry - bare rock and rubble
  level9: {
    large: [{ image: `${OBJ}/Ruin2_grass_shadow.png`, size: 4 }],
    largeCount: 2,
    medium: [...ROCKS_MEDIUM, ...STONES_MEDIUM],
    mediumCount: 11,
    small: [...STONES_SMALL, ...DEAD_TREES],
    smallCount: 16,
    details: [
      { type: "dirt_speckles", weight: 4 },
      { type: "stones_small", weight: 4 },
      { type: "dirt_mound", weight: 3 },
      { type: "dirt_clearing", weight: 2 },
      { type: "rock_form_dirt", weight: 1 },
    ],
    detailCount: 28,
  },
  // Necropolis - ruins and cold blue light
  level10: {
    large: RUINS_LARGE,
    largeCount: 4,
    medium: [...RUINS_MEDIUM, ...STONES_MEDIUM],
    mediumCount: 10,
    small: [...RUIN_SMALL, ...STONES_SMALL, ...DEAD_TREES],
    smallCount: 14,
    details: [
      { type: "crystal_blue", weight: 2 },
      { type: "sparkle", weight: 3 },
      { type: "dirt_speckles", weight: 3 },
      { type: "stones_small", weight: 2 },
    ],
    detailCount: 24,
  },
  // Castle Crimson - fortress walls, red growth
  level11: {
    large: RUINS_LARGE,
    largeCount: 5,
    medium: [...RUINS_MEDIUM, ...STONES_MEDIUM],
    mediumCount: 10,
    small: [...RUIN_SMALL, ...STONES_SMALL],
    smallCount: 12,
    details: [
      { type: "red_plant", weight: 3 },
      { type: "red_plant_spiky", weight: 3 },
      { type: "flowers_red", weight: 2 },
      { type: "dirt_speckles", weight: 2 },
    ],
    detailCount: 26,
  },
  // Ancient Grove - old-growth forest in bloom
  level12: {
    large: TREES,
    largeCount: 8,
    medium: [...BUSHES_MEDIUM, { image: `${OBJ}/Tree5.png`, size: 2 }],
    mediumCount: 11,
    small: [...BUSHES_SMALL, ...FLOWERS_SMALL, ...GRASS_ELEMENTS],
    smallCount: 16,
    details: [...MEADOW_DETAILS, { type: "sparkle", weight: 2 }, { type: "bush_berry_blue", weight: 1 }],
    detailCount: 34,
  },
  // The Void - empty, crystalline
  level13: {
    large: [],
    largeCount: 0,
    medium: STONES_MEDIUM,
    mediumCount: 6,
    small: [...STONES_SMALL, ...DEAD_TREES],
    smallCount: 10,
    details: [
      { type: "crystal_blue", weight: 3 },
      { type: "sparkle", weight: 4 },
      { type: "dirt_speckles", weight: 2 },
      { type: "rock_form_grass", weight: 1 },
    ],
    detailCount: 22,
  },
  // The Final Stand - everything at once
  level14: {
    large: [...TREES.slice(0, 2), ...RUINS_LARGE],
    largeCount: 6,
    medium: [...RUINS_MEDIUM, ...ROCKS_MEDIUM, ...BUSHES_MEDIUM.slice(0, 2)],
    mediumCount: 10,
    small: [...STONES_SMALL, ...DEAD_TREES, ...RUIN_SMALL],
    smallCount: 14,
    details: [...MYSTIC_DETAILS, { type: "red_plant", weight: 1 }],
    detailCount: 28,
  },
}
